import axios from "axios";
import { getCookie } from "cookies-next";
import { useCallback, useEffect, useRef, useState } from "react";
import { BiChevronLeft, BiChevronRight } from "react-icons/bi";
import { FaPlus } from "react-icons/fa";
import { FaXmark } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { Header, PageLoading } from "../../../components";
import { API_ROOT } from "../../../config";
import { useStateContext } from "../../../contexts/ContextProvider";
import BlankProfile from "../../../data/blank.png";
import "../../table.css";

const LIMIT_OPTIONS = [10, 25, 50, 100];

const formatDateTime = (value) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString();
  } catch (e) {
    return value;
  }
};

const formatNumber = (value) => {
  if (value === null || value === undefined) return "-";
  return Number(value).toLocaleString("id-ID");
};

const toLocalDatetimeInputValue = (date) => {
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
};

const TopupTransactionLog = () => {
  const navigate = useNavigate();
  const { currentColor } = useStateContext();
  const abortRef = useRef(null);

  const [pageLoading, setPageLoading] = useState(true);
  const [gridLoading, setGridLoading] = useState(false);
  const [logs, setLogs] = useState([]);

  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [currentLimit, setCurrentLimit] = useState(10);

  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  // ADD TRANSACTION MODAL
  const [toggleAdd, setToggleAdd] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [addWarning, setAddWarning] = useState({});
  const [addData, setAddData] = useState(null);

  const fetchData = useCallback(
    async (page, limit, search) => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setGridLoading(true);
      try {
        const response = await axios.get(
          API_ROOT + "/admin/topup-transaction-log/list",
          {
            signal: controller.signal,
            headers: { Authorization: getCookie("admin_auth") },
            params: { page, limit, search: search || undefined },
          },
        );

        const resData = response.data.data;
        const rows = resData?.data ?? [];
        const rawPg = resData?.pagination ?? {};

        const pg = {
          total: rawPg.total ?? 0,
          page: rawPg.page ?? page,
          limit: rawPg.limit ?? limit,
          totalPages:
            rawPg.totalPages ??
            Math.max(Math.ceil((rawPg.total ?? 0) / (rawPg.limit ?? limit)), 1),
        };

        setLogs(
          rows.map((item, index) => ({
            No: (pg.page - 1) * pg.limit + index + 1,
            Uuid: item.uuid,
            UserName: item.user?.name ?? "-",
            Username: item.user?.username ?? "-",
            Amount: item.amount,
            BalanceBefore: item.balance_before,
            BalanceAfter: item.balance_after,
            Notes: item.notes ?? "-",
            TopupAt: item.topup_at,
            CreatedAt: item.created_at,
            CreatedBy: item.creator?.name ?? "-",
          })),
        );
        setPagination(pg);
      } catch (error) {
        if (error.code === "ERR_CANCELED" || axios.isCancel(error)) return;
        if (error.response?.status === 401) return navigate("/login");
        toast.error("Failed to load topup transaction log data");
      } finally {
        setPageLoading(false);
        setGridLoading(false);
      }
    },
    [navigate],
  );

  useEffect(() => {
    fetchData(currentPage, currentLimit, appliedSearch);
  }, [currentPage, currentLimit, appliedSearch, fetchData]);

  const handleSearch = () => {
    setAppliedSearch(searchInput.trim());
    setCurrentPage(1);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleLimitChange = (e) => {
    setCurrentLimit(Number(e.target.value));
    setCurrentPage(1);
  };

  // ---- Search user (debounced) untuk form tambah transaksi ----
  useEffect(() => {
    async function searchUser(value) {
      if (!value) {
        setAddData((prev) => (prev ? { ...prev, user_list: [] } : prev));
        return;
      }
      try {
        const response = await axios.get(
          API_ROOT + "/admin/user/search-topup?keywords=" + value,
          { headers: { Authorization: getCookie("admin_auth") } },
        );
        setAddData((prev) =>
          prev ? { ...prev, user_list: response.data.data } : prev,
        );
      } catch (error) {
        if (error.response?.status === 401) {
          navigate("/login");
          return;
        }
        setAddData((prev) => (prev ? { ...prev, user_list: [] } : prev));
        toast.error("Failed to get user data");
      }
    }

    const timeout = setTimeout(() => {
      if (addData) searchUser(addData.user_temp);
    }, 500);

    return () => clearTimeout(timeout);
  }, [addData && addData.user_temp, navigate]);

  const openAddModal = () => {
    setAddData({
      selected_user: null,
      user_temp: "",
      user_list: [],
      amount: "",
      notes: "",
      topup_at: toLocalDatetimeInputValue(new Date()),
    });
    setAddWarning({});
    setToggleAdd(true);
  };

  const balanceBefore = Number(addData?.selected_user?.balance ?? 0);
  const amountNumber = Number(addData?.amount) || 0;
  const balanceAfter = balanceBefore + amountNumber;

  const handleSubmitAdd = async () => {
    const warnings = {};

    if (!addData.selected_user) warnings.selected_user = "User wajib dipilih";
    if (!addData.amount || Number(addData.amount) <= 0)
      warnings.amount = "Jumlah top up harus lebih dari 0";
    if (!addData.topup_at) warnings.topup_at = "Tanggal top up wajib diisi";

    if (Object.keys(warnings).length > 0) {
      setAddWarning(warnings);
      return;
    }

    setAddLoading(true);
    try {
      await axios.post(
        API_ROOT + "/admin/topup-transaction-log/create",
        {
          user_id: addData.selected_user.id,
          amount: Number(addData.amount),
          notes: addData.notes || null,
          topup_at: new Date(addData.topup_at).toISOString(),
        },
        { headers: { Authorization: getCookie("admin_auth") } },
      );

      toast.success("Topup transaction created!");
      setToggleAdd(false);
      setCurrentPage(1);
      fetchData(1, currentLimit, appliedSearch);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/login");
      } else {
        toast.error(
          error.response?.data?.err?.message ||
            "Failed to create topup transaction",
        );
      }
    } finally {
      setAddLoading(false);
    }
  };

  const { page, totalPages, total } = pagination;

  return pageLoading ? (
    <PageLoading />
  ) : (
    <div className="m-2 md:m-10 mt-24 px-2 py-10 md:p-10 bg-white rounded-3xl">
      <div className="flex justify-between items-center pb-4">
        <Header title="Topup Transaction Log" />
        <button
          style={{ backgroundColor: currentColor }}
          className="text-white w-44 py-2 px-4 rounded-md text-sm"
          onClick={openAddModal}
        >
          Tambah Transaksi
        </button>
      </div>

      <div className="flex flex-col gap-4">
        <hr />

        <div className="flex mb-2 gap-4 items-end">
          <div className="flex-1 max-w-sm">
            <label className="block text-sm mb-1">Cari</label>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Cari nama atau username user..."
              className="block border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            className="text-white w-44 py-2 px-4 rounded-md text-sm bg-green-500 hover:bg-green-600"
            onClick={handleSearch}
          >
            Filter
          </button>
        </div>

        <div className="overflow-x-auto relative">
          {gridLoading && (
            <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center">
              Loading...
            </div>
          )}
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gray-100 text-left">
                <th className="p-2 border">No</th>
                <th className="p-2 border">Nama User</th>
                <th className="p-2 border">Username</th>
                <th className="p-2 border text-right">Amount</th>
                <th className="p-2 border text-right">Balance Before</th>
                <th className="p-2 border text-right">Balance After</th>
                <th className="p-2 border">Notes</th>
                <th className="p-2 border">Topup At</th>
                <th className="p-2 border">Created At</th>
                <th className="p-2 border">Created By</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr>
                  <td colSpan={11} className="p-4 text-center text-gray-500">
                    Tidak ada data
                  </td>
                </tr>
              ) : (
                logs.map((row) => (
                  <tr key={row.Uuid} className="hover:bg-gray-50">
                    <td className="p-2 border text-center">{row.No}</td>
                    <td className="p-2 border">{row.UserName}</td>
                    <td className="p-2 border">{row.Username}</td>
                    <td className="p-2 border text-right">
                      {formatNumber(row.Amount)}
                    </td>
                    <td className="p-2 border text-right">
                      {formatNumber(row.BalanceBefore)}
                    </td>
                    <td className="p-2 border text-right">
                      {formatNumber(row.BalanceAfter)}
                    </td>
                    <td className="p-2 border">{row.Notes}</td>
                    <td className="p-2 border text-xs">
                      {formatDateTime(row.TopupAt)}
                    </td>
                    <td className="p-2 border text-xs">
                      {formatDateTime(row.CreatedAt)}
                    </td>
                    <td className="p-2 border">{row.CreatedBy}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between mt-2 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <span>Tampilkan</span>
            <select
              value={currentLimit}
              onChange={handleLimitChange}
              className="border border-gray-300 rounded px-2 py-1"
            >
              {LIMIT_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            <span>
              data per halaman · Total <strong>{total}</strong> data
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-40"
            >
              <BiChevronLeft size={20} />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(
                (p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1,
              )
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                acc.push(p);
                return acc;
              }, [])
              .map((item, idx) =>
                item === "..." ? (
                  <span key={`e-${idx}`} className="px-2">
                    …
                  </span>
                ) : (
                  <button
                    key={item}
                    onClick={() => setCurrentPage(item)}
                    className={`w-8 h-8 rounded text-sm ${
                      item === page
                        ? "bg-green-500 text-white"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    {item}
                  </button>
                ),
              )}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-40"
            >
              <BiChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* MODAL TAMBAH TRANSAKSI */}
      {toggleAdd && (
        <div className="overflow-auto fixed z-[999999999999] inset-0 flex justify-center w-full min-h-screen h-full py-20 px-4 md:px-[100px] bg-slate-800/50">
          <div className="bg-white flex flex-col gap-2 w-full max-w-[480px] h-fit rounded-xl p-8">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-medium">Tambah Transaksi Top Up</h1>
              <div
                className="cursor-pointer p-2"
                onClick={() => setToggleAdd(false)}
              >
                &times;
              </div>
            </div>
            <hr />
            <div className="flex flex-col gap-4 w-full">
              {/* USER SELECTOR */}
              <div className="flex flex-col gap-2">
                <h4 className="font-medium">User</h4>
                {addData.selected_user ? (
                  <div className="flex justify-between items-center bg-white py-3 px-4 border border-slate-500 rounded-md">
                    <div className="flex gap-2 items-center">
                      <img
                        src={
                          addData.selected_user.image
                            ? "data:image/*;base64," +
                              addData.selected_user.image
                            : BlankProfile
                        }
                        alt="User profile"
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div className="text-sm">
                        <h1>{addData.selected_user.name}</h1>
                        <h3 className="text-slate-700 font-semibold">
                          {"@" + addData.selected_user.username}
                        </h3>
                      </div>
                    </div>
                    <button
                      className="p-2 cursor-pointer"
                      onClick={() =>
                        setAddData((prev) => ({ ...prev, selected_user: null }))
                      }
                    >
                      <FaXmark className="w-3 h-3 text-slate-800" />
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      placeholder="Cari User"
                      spellCheck="false"
                      className={
                        "w-full p-2 text-sm rounded-[4px] border focus:outline-2 " +
                        (!addWarning.selected_user
                          ? "border-slate-500 focus:outline-sky-500"
                          : "border-red-500 focus:outline-red-500")
                      }
                      value={addData.user_temp}
                      onChange={(e) => {
                        setAddData((prev) => ({
                          ...prev,
                          user_temp: e.target.value,
                        }));
                        setAddWarning((prev) => ({
                          ...prev,
                          selected_user: null,
                        }));
                      }}
                    />
                    {addData.user_list.length !== 0 && (
                      <div className="flex flex-col w-full max-h-[192px] border border-slate-500 overflow-y-auto rounded-md cursor-pointer">
                        {addData.user_list.map((item, index) => (
                          <div
                            key={"user" + index}
                            className="py-3 px-4 flex justify-between items-center bg-white hover:bg-slate-200 active:bg-slate-50"
                            onClick={() => {
                              setAddData((prev) => ({
                                ...prev,
                                selected_user: {
                                  ...item,
                                  balance: Number(item.balance ?? 0),
                                },
                                user_temp: "",
                                user_list: [],
                              }));
                              setAddWarning((prev) => ({
                                ...prev,
                                selected_user: null,
                              }));
                            }}
                          >
                            <div className="flex gap-2 items-center">
                              <img
                                src={
                                  item.image
                                    ? "data:image/*;base64," + item.image
                                    : BlankProfile
                                }
                                alt="User profile"
                                className="w-10 h-10 rounded-full object-cover"
                              />
                              <div className="text-sm">
                                <h1>{item.name}</h1>
                                <h3 className="text-slate-700 font-semibold">
                                  {"@" + item.username}
                                </h3>
                                <p className="text-xs text-slate-500">
                                  Balance: {formatNumber(item.balance)}
                                  {item.platform_name
                                    ? ` · ${item.platform_name}`
                                    : ""}
                                </p>
                              </div>
                            </div>
                            <FaPlus className="w-3 h-3 text-slate-800" />
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
                {addWarning.selected_user && (
                  <p className="text-xs text-red-500">
                    {addWarning.selected_user}
                  </p>
                )}
              </div>

              {/* BALANCE BEFORE (readonly) */}
              <div className="flex flex-col gap-2">
                <h4 className="font-medium">Balance Sebelum</h4>
                <input
                  type="text"
                  disabled
                  value={formatNumber(balanceBefore)}
                  className="w-full p-2 text-sm rounded-[4px] border border-slate-300 bg-gray-100"
                />
              </div>

              {/* AMOUNT */}
              <div className="flex flex-col gap-2">
                <h4 className="font-medium">Total Top Up</h4>
                <input
                  type="number"
                  min="1"
                  placeholder="Input jumlah top up..."
                  className={
                    "w-full p-2 text-sm rounded-[4px] border focus:outline-2 " +
                    (!addWarning.amount
                      ? "border-slate-500 focus:outline-sky-500"
                      : "border-red-500 focus:outline-red-500")
                  }
                  value={addData.amount}
                  onChange={(e) => {
                    setAddData((prev) => ({ ...prev, amount: e.target.value }));
                    setAddWarning((prev) => ({ ...prev, amount: null }));
                  }}
                />
                {addWarning.amount && (
                  <p className="text-xs text-red-500">{addWarning.amount}</p>
                )}
              </div>

              {/* BALANCE AFTER (readonly, realtime) */}
              <div className="flex flex-col gap-2">
                <h4 className="font-medium">Balance Sesudah</h4>
                <input
                  type="text"
                  disabled
                  value={formatNumber(balanceAfter)}
                  className="w-full p-2 text-sm rounded-[4px] border border-slate-300 bg-gray-100 font-semibold"
                />
              </div>

              {/* TOPUP AT */}
              <div className="flex flex-col gap-2">
                <h4 className="font-medium">Waktu Top Up</h4>
                <input
                  type="datetime-local"
                  className={
                    "w-full p-2 text-sm rounded-[4px] border focus:outline-2 " +
                    (!addWarning.topup_at
                      ? "border-slate-500 focus:outline-sky-500"
                      : "border-red-500 focus:outline-red-500")
                  }
                  value={addData.topup_at}
                  onChange={(e) => {
                    setAddData((prev) => ({
                      ...prev,
                      topup_at: e.target.value,
                    }));
                    setAddWarning((prev) => ({ ...prev, topup_at: null }));
                  }}
                />
                {addWarning.topup_at && (
                  <p className="text-xs text-red-500">{addWarning.topup_at}</p>
                )}
              </div>

              {/* NOTES */}
              <div className="flex flex-col gap-2">
                <h4 className="font-medium">Notes</h4>
                <textarea
                  placeholder="Catatan tambahan (opsional)..."
                  maxLength={255}
                  className="w-full p-2 text-sm rounded-[4px] border border-slate-500 focus:outline-2 focus:outline-sky-500"
                  value={addData.notes}
                  onChange={(e) =>
                    setAddData((prev) => ({ ...prev, notes: e.target.value }))
                  }
                />
              </div>

              <div className="flex justify-end items-center">
                <button
                  disabled={addLoading}
                  style={{ backgroundColor: currentColor }}
                  className="text-white w-36 mt-4 py-2 px-4 rounded-md text-sm"
                  onClick={handleSubmitAdd}
                >
                  {!addLoading ? "Simpan" : "Loading..."}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopupTransactionLog;
